/**
 * CountPanel — the live COUNT palette (Faz 5.9, OCS count_palette.rs parity):
 * an Item|Count table driven directly by the entity map, so INSERT/DELETE
 * update the tallies without any bookkeeping (the OCS geometry_epoch
 * freshness expressed reactively). Prev/Next cycle the cursor through the
 * matching references and zoom to each.
 */

import { Box, Flex, IconButton, Text, VStack } from '@chakra-ui/react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../state/store';
import { useCountStore } from '../../state/commandAuxStores';
import { tallyBlocks } from '../../core/commands/runners/blockCommands';

export function CountPanel() {
    const { t } = useTranslation();
    const session = useCountStore((s) => s.session);
    const stop = useCountStore((s) => s.stop);
    const entities = useAppStore((s) => s.entities);
    const select = useAppStore((s) => s.select);

    // Live tally: recomputed on every entity-map change (INSERT/DELETE) and
    // on session changes. Empty session rows collapse away.
    const tally = useMemo(
        () => (session ? tallyBlocks(session.target, session.area) : []),
        [session, entities],
    );
    const total = useMemo(() => tally.reduce((acc, g) => acc + g.count, 0), [tally]);

    // Prev/Next cursor over the flat id list of the current tally.
    const [cursor, setCursor] = useState(0);
    const flatIds = useMemo(() => tally.flatMap((g) => g.ids), [tally]);
    useEffect(() => {
        if (cursor >= flatIds.length) setCursor(0);
    }, [flatIds.length, cursor]);

    const zoomTo = (index: number) => {
        if (flatIds.length === 0) return;
        const wrapped = ((index % flatIds.length) + flatIds.length) % flatIds.length;
        setCursor(wrapped);
        select([flatIds[wrapped]]);
        window.dispatchEvent(new CustomEvent('yap:zoom-to-selection'));
    };

    if (!session) {
        return (
            <Box p={3}>
                <Text fontSize="xs" color="text.secondary">
                    {t('ui.cad.count.noSession')}
                </Text>
            </Box>
        );
    }

    return (
        <Box h="full" display="flex" flexDirection="column" minW={0}>
            {/* Session header: target + area + stop */}
            <Flex px={2} py={2} gap={2} align="center" borderBottom="1px solid" borderColor="border.base" flexShrink={0} minW={0}>
                <Text fontSize="xs" color="text.primary" noOfLines={1} minW={0} flex="1">
                    {session.target
                        ? t('ui.cad.count.sessionTarget', { name: session.target })
                        : t('ui.cad.count.sessionAll')}
                    {session.area ? ` · ${t('ui.cad.count.areaFiltered')}` : ''}
                </Text>
                <IconButton
                    size="xs"
                    variant="ghost"
                    aria-label={t('ui.cad.count.stop')}
                    icon={<X size={12} />}
                    onClick={() => stop()}
                    flexShrink={0}
                />
            </Flex>

            {/* Item | Count table */}
            <Box flex="1" minH="0" overflowY="auto" overflowX="hidden">
                <VStack spacing={0} align="stretch" px={1}>
                    <Flex px={2} py={1} borderBottom="1px solid" borderColor="border.base" flexShrink={0}>
                        <Text fontSize="10px" color="text.secondary" textTransform="uppercase" flex="1">
                            {t('ui.cad.count.colItem')}
                        </Text>
                        <Text fontSize="10px" color="text.secondary" textTransform="uppercase" w="56px" textAlign="right" flexShrink={0}>
                            {t('ui.cad.count.colCount')}
                        </Text>
                        <Text fontSize="10px" color="text.secondary" textTransform="uppercase" w="56px" textAlign="right" flexShrink={0}>
                            {t('ui.cad.count.colZoom')}
                        </Text>
                    </Flex>
                    {tally.length === 0 && (
                        <Text px={2} py={3} fontSize="xs" color="text.secondary">
                            {t('ui.cad.count.noMatches')}
                        </Text>
                    )}
                    {tally.map((group) => (
                        <Flex key={group.name} px={2} py={1.5} align="center" _hover={{ bg: 'bg.hover' }} borderRadius="4px">
                            <Text fontSize="xs" color="text.primary" noOfLines={1} minW={0} flex="1">
                                {group.name}
                            </Text>
                            <Text fontSize="xs" color="text.primary" w="56px" textAlign="right" flexShrink={0} fontFamily="mono">
                                {group.count}
                            </Text>
                            <Flex w="56px" justify="flex-end" flexShrink={0}>
                                <IconButton
                                    size="xs"
                                    variant="ghost"
                                    aria-label={t('ui.cad.count.zoomPrev')}
                                    icon={<ChevronLeft size={12} />}
                                    px={0}
                                    onClick={() => zoomTo(cursor - 1)}
                                />
                                <IconButton
                                    size="xs"
                                    variant="ghost"
                                    aria-label={t('ui.cad.count.zoomNext')}
                                    icon={<ChevronRight size={12} />}
                                    px={0}
                                    onClick={() => zoomTo(cursor + 1)}
                                />
                            </Flex>
                        </Flex>
                    ))}
                </VStack>
            </Box>

            {/* Footer: live total */}
            <Flex px={2} py={2} borderTop="1px solid" borderColor="border.base" flexShrink={0}>
                <Text fontSize="xs" color="text.secondary">
                    {t('ui.cad.count.total', { n: total })}
                </Text>
            </Flex>
        </Box>
    );
}
